import { I18n } from "@assets/strings";
import React, { useEffect } from "react";
import { Link } from "react-router";
import { Redirect } from "@core/routing/redirect";
import { ClipLoader } from "react-spinners";
import styled from "styled-components";
import { Path } from "@core/routing/path";
import { useServices } from "@core/servicesContext";
import { useCurrentProfile, useIsPerson } from "@domain/currentProfile/hooks";
import { useCourse } from "@domain/lms/course/hooks";
import { AppAuthIncentive } from "@ui/components/appAuthIncentive";
import { EditIcon, FavoriteOffIcon } from "@ui/components/icons";
import { stack } from "@ui/components/layout";
import { Page } from "@ui/components/page";
import { NotFoundPage } from "@ui/pages/notFoundPage";
import { CoursePresentation } from "./coursePresentation";

type CoursePageProps =
	| (React.HTMLAttributes<HTMLDivElement> & {
			courseId: string;
			courseSlug?: undefined;
			action?: "NEW_COURSE_EMAIL" | "FEEDBACK_COURS_EMAIL";
	  })
	| {
			courseSlug: string;
			courseId?: undefined;
			action?: "NEW_COURSE_EMAIL" | "FEEDBACK_COURS_EMAIL";
	  };
export const CoursePage: React.FC<CoursePageProps> = ({ courseId, courseSlug, action, ...htmlProps }) => {
	if (!courseId && !courseSlug) {
		return <Redirect to={Path.Root} />;
	}
	const { courseService } = useServices();
	const currentProfile = useCurrentProfile();
	const { loading, result: course, error } = useCourse(courseId ?? "", courseSlug);
	const isAuthor = useIsPerson(course?.author ?? {});
	const { trackingService } = useServices();

	useEffect(() => {
		if (action === "NEW_COURSE_EMAIL") {
			trackingService.track("trackEmail - NEW_COURSE_EMAIL");
		}
		if (action === "FEEDBACK_COURS_EMAIL") {
			trackingService.track("trackEmail - FEEDBACK_COURS_EMAIL");
		}
	}, []);

	if (error?.code === 404 && !isAuthor && !course && !loading) {
		return <NotFoundPage message={I18n.t("404.course")} />;
	}

	return !course ? (
		loading ? (
			<ClipLoader />
		) : null
	) : (
		<Container
			{...htmlProps}
			header={
				course ? (
					isAuthor ? (
						<Link to={Path.Lms.Editor(course.id)}>
							<HeaderAction>
								{I18n.t("edit")}
								<EditIcon />
							</HeaderAction>
						</Link>
					) : (
						<HeaderAction onClick={() => courseService.toggleFavorite(course)}>
							<FavoriteOffIcon toggled={course.isFavorite} />
						</HeaderAction>
					)
				) : (
					<></>
				)
			}
		>
			{/* <HelmetInfos
				title={I18n.t("seo.course.title", { name: course.title })}
				description={[
					course.author && fullName(course.author),
					I18n.t(`category.${course.category}`),
					course.description.getPlainText(),
				]
					.filter(Boolean)
					.join(" - ")}
				canonicalLink={course.reference ?? `${Config.APP_BASE_URL}${location.pathname}`}
			/> */}
			{/* <Seo
				type="website"
				title={I18n.t("seo.course.title", { name: course.title })}
				description={course.description.getPlainText()}
				ogImage={!course.banner ? require("../../../../assets/images/default-thumbnail.jpg") : course.banner?.url}
				canonicalLink={course.reference ?? `${Config.APP_BASE_URL}${location.pathname}`}
			/> */}
			<CoursePresentation course={course} />
			{!currentProfile && <Space />}
			{!currentProfile && <AppAuthIncentive message={I18n.t("course.auth.incentive", { name: course.title })} />}
		</Container>
	);
};

const Container = styled(Page)`
	${stack(0, "flex-start", "center")};
`;

const HeaderAction = styled.div`
	display: flex;
	align-items: center;
	${({ theme }) => theme.textStyles.primary};
	cursor: pointer;
	svg {
		margin-left: 5px;
		path {
			transition: fill 0.2s linear;
		}
	}
`;

const Space = styled.div`
	height: 167px;
`;
